/**
 * Command-based, pure document operations. Every function returns a NEW document and never mutates
 * its input; untouched subtrees are shared by reference (structural sharing) so React/Zustand can
 * diff cheaply and the History can keep many snapshots.
 */
import {
  PhotoElement,
  TextElement,
  Spread as SpreadSchema,
  type AlbumDocument,
  type AlbumMeta,
  type AlbumTheme,
  type Background,
  type Cover,
  type Element,
  type Frame,
  type PageSize,
  type PrintTarget,
  type Spread,
  type TextStyle,
} from './schema';
import { PAGE_COUNT_LIMITS, defaultSpineWidthMm, type FormatId } from './formats';
import { emptySpread, innerPageCount, normalizeSingles, spineRotationDeg, spineTextDirection } from './helpers';
import { makePhotoElement, makeTextElement } from './elements';
import { TAG_LIMITS, normalizeTags, sameTags } from './tags';
import type { Layout, LayoutSlot } from './layout-types';

type AlbumMetaT = ReturnType<typeof AlbumMeta.parse>;

export type DeepPartial<T> = T extends (infer U)[]
  ? U[]
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

export type ElementTarget = number | 'cover';

export type Command =
  | { type: 'spread.add'; index: number; spread?: Spread }
  | { type: 'spread.remove'; index: number }
  | { type: 'spread.move'; from: number; to: number }
  | { type: 'spread.setBackground'; index: number | 'cover'; background: Background; side?: 'both' | 'right' }
  /** Replace the spread's tags. Normalised and de-duplicated; a no-op when nothing changes. */
  | { type: 'spread.setTags'; index: number; tags: string[] }
  | {
      type: 'spread.applyLayout';
      index: number;
      layout: Layout;
      photoIds?: string[];
      page?: 'left' | 'right' | 'both';
      keepExisting?: boolean;
    }
  | {
      /** Lay a template onto the cover: a cover template on its own panels, or a page/spread template onto `target`. */
      type: 'cover.applyLayout';
      layout: Layout;
      target: CoverLayoutTarget;
      photoIds?: string[];
      keepExisting?: boolean;
    }
  | { type: 'element.add'; target: ElementTarget; element: Element }
  | { type: 'element.update'; target: ElementTarget; id: string; patch: DeepPartial<Element> }
  | { type: 'element.remove'; target: ElementTarget; ids: string[] }
  | { type: 'element.reorder'; target: ElementTarget; id: string; to: 'front' | 'back' | 'forward' | 'backward' }
  | { type: 'element.moveToSpread'; from: number; to: number; id: string }
  | { type: 'album.setFormat'; format: FormatId; page: PageSize }
  | { type: 'album.setPageCount'; innerPages: number }
  | { type: 'album.setMeta'; patch: Partial<AlbumMetaT> }
  /** Where the album will be printed and how it is bound (`PrintTarget`); drives guides and the export default. */
  | { type: 'album.setPrint'; patch: Partial<PrintTarget> }
  | {
      type: 'album.setTheme';
      patch: Partial<AlbumTheme>;
      /**
       * Also move existing content that still uses the outgoing theme's palette colours and fonts
       * onto the new ones (UX-34). Off by default: the command then only changes what NEW content
       * inherits. Custom colours and fonts are preserved either way.
       */
      restyle?: boolean;
    }
  | {
      type: 'cover.set';
      patch: Partial<Pick<Cover, 'enabled' | 'spineWidthMm' | 'spineWidthMode' | 'spineTextDirection' | 'spineBackground' | 'binding'>>;
    }
  | { type: 'batch'; commands: Command[] };

export class CommandError extends Error {
  constructor(message: string, readonly command: Command) {
    super(message);
    this.name = 'CommandError';
  }
}

// ---------- generic helpers ----------

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Deep-merge `patch` into `base`. Arrays and non-object values replace; objects recurse. */
export function deepMerge<T>(base: T, patch: DeepPartial<T> | undefined): T {
  if (patch === undefined) return base;
  if (!isPlainObject(base) || !isPlainObject(patch)) return patch as T;
  let changed = false;
  const out: Record<string, unknown> = { ...base };
  for (const key of Object.keys(patch)) {
    const pv = (patch as Record<string, unknown>)[key];
    if (pv === undefined) continue;
    const merged = deepMerge(base[key], pv as never);
    if (merged !== base[key]) {
      out[key] = merged;
      changed = true;
    }
  }
  return changed ? (out as T) : base;
}

function replaceAt<T>(arr: readonly T[], index: number, value: T): T[] {
  if (arr[index] === value) return arr as T[];
  const copy = arr.slice();
  copy[index] = value;
  return copy;
}

export function sortElements(elements: readonly Element[]): Element[] {
  return elements
    .map((el, i) => ({ el, i }))
    .sort((a, b) => a.el.z - b.el.z || a.i - b.i)
    .map((x) => x.el);
}

function requireSpread(doc: AlbumDocument, index: number, cmd: Command): Spread {
  const s = doc.spreads[index];
  if (!s) throw new CommandError(`spread ${index} does not exist`, cmd);
  return s;
}

function getElements(doc: AlbumDocument, target: ElementTarget, cmd: Command): Element[] {
  return target === 'cover' ? doc.cover.elements : requireSpread(doc, target, cmd).elements;
}

function withElements(doc: AlbumDocument, target: ElementTarget, elements: Element[], cmd: Command): AlbumDocument {
  if (target === 'cover') {
    if (elements === doc.cover.elements) return doc;
    return { ...doc, cover: { ...doc.cover, elements } };
  }
  const spread = requireSpread(doc, target, cmd);
  if (elements === spread.elements) return doc;
  return { ...doc, spreads: replaceAt(doc.spreads, target, { ...spread, elements }) };
}

function withSpread(doc: AlbumDocument, index: number, spread: Spread): AlbumDocument {
  return { ...doc, spreads: replaceAt(doc.spreads, index, spread) };
}

// ---------- geometry ----------

export interface BoxMm {
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
}

/** The mm box a layout scope maps onto. Single spreads always use their one existing page. */
export function scopeBox(doc: Pick<AlbumDocument, 'page'>, spread: Pick<Spread, 'single'>, scope: Layout['scope'], page?: 'left' | 'right'): BoxMm {
  const { widthMm, heightMm } = doc.page;
  const side = spread.single ?? page;
  if (scope === 'spread' && !spread.single) return { xMm: 0, yMm: 0, wMm: widthMm * 2, hMm: heightMm };
  return { xMm: side === 'right' ? widthMm : 0, yMm: 0, wMm: widthMm, hMm: heightMm };
}

export function slotToFrame(slot: LayoutSlot, box: BoxMm): Frame {
  const r = (n: number) => Math.round(n * 100) / 100;
  return {
    xMm: r(box.xMm + slot.x * box.wMm),
    yMm: r(box.yMm + slot.y * box.hMm),
    wMm: r(Math.max(0.1, slot.w * box.wMm)),
    hMm: r(Math.max(0.1, slot.h * box.hMm)),
    rotationDeg: 0,
  };
}

const ROLE_KIND: Record<NonNullable<LayoutSlot['role']>, TextElement['kind']> = {
  title: 'title',
  caption: 'caption',
  body: 'text',
};

/** Default typography for a layout text slot, derived from the album theme. */
export function slotTextStyle(theme: AlbumTheme, role: LayoutSlot['role']): Partial<TextStyle> {
  const color = theme.palette[1] ?? '#1b1b1f';
  switch (role) {
    case 'title':
      return { fontFamily: theme.headingFont, fontSizePt: 28, align: 'center', color, verticalAlign: 'middle' };
    case 'caption':
      return { fontFamily: theme.bodyFont, fontSizePt: 10, align: 'center', color, verticalAlign: 'middle' };
    default:
      return { fontFamily: theme.bodyFont, fontSizePt: 12, align: 'start', color, verticalAlign: 'top' };
  }
}

export type CoverPanel = 'front' | 'back' | 'spine';
/** Where a non-cover template lands on the cover: one panel, or the whole strip. */
export type CoverLayoutTarget = 'front' | 'back' | 'strip';

/** The mm box of one cover panel or of the whole strip. Binding decides which panel comes first. */
export function coverPanelBoxMm(doc: Pick<AlbumDocument, 'page' | 'cover'>, part: CoverPanel | 'strip'): BoxMm {
  const w = doc.page.widthMm;
  const h = doc.page.heightMm;
  const spine = doc.cover.spineWidthMm;
  if (part === 'strip') return { xMm: 0, yMm: 0, wMm: w * 2 + spine, hMm: h };
  if (part === 'spine') return { xMm: w, yMm: 0, wMm: spine, hMm: h };
  const first: CoverPanel = doc.cover.binding === 'rtl' ? 'front' : 'back';
  if (part === first) return { xMm: 0, yMm: 0, wMm: w, hMm: h };
  return { xMm: w + spine, yMm: 0, wMm: w, hMm: h };
}

/** The box one slot of `layout` maps onto when the layout is applied to the cover at `target`. */
export function coverSlotBoxMm(doc: Pick<AlbumDocument, 'page' | 'cover'>, layout: Layout, slot: LayoutSlot, target: CoverLayoutTarget): BoxMm {
  if (layout.scope === 'cover') return coverPanelBoxMm(doc, slot.panel ?? 'strip');
  if (layout.scope === 'page') return coverPanelBoxMm(doc, target === 'strip' ? 'front' : target);
  return coverPanelBoxMm(doc, 'strip');
}

/** The part of the cover a template replaces: its own panels for a cover template, else `target`. */
export function coverLayoutRegion(layout: Layout, target: CoverLayoutTarget): CoverPanel | 'strip' {
  if (layout.scope === 'spread') return 'strip';
  if (layout.scope === 'page') return target === 'strip' ? 'front' : target;
  const panels = new Set(layout.slots.map((s) => s.panel ?? 'strip'));
  if (panels.size !== 1) return 'strip';
  return [...panels][0] as CoverPanel | 'strip';
}

function isLightColor(hex: string): boolean {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const h = m[1]!.length === 3 ? m[1]!.split('').map((c) => c + c).join('') : m[1]!;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6;
}

/** Cover text sits on something dark more often than not: white, unless the cover colour is light. */
export function coverTextColor(cover: Pick<Cover, 'background'>): string {
  const bg = cover.background;
  return bg.type === 'color' && isLightColor(bg.color) ? '#1b1b1f' : '#ffffff';
}

export interface MaterializeCoverOptions {
  photoIds?: string[];
  /** reuse the photos (with their crops) and slot text already in the replaced region */
  keepExisting?: boolean;
}

/**
 * The cover's counterpart of `materializeLayout`. Returns the new elements for the region the
 * template covers plus the elements outside it, which are left exactly as they were. Spine text is
 * never part of a layout and always survives.
 */
export function materializeCoverLayout(
  doc: AlbumDocument,
  layout: Layout,
  target: CoverLayoutTarget,
  opts: MaterializeCoverOptions = {},
): { elements: Element[]; kept: Element[] } {
  const region = coverLayoutRegion(layout, target);
  const regionBox = coverPanelBoxMm(doc, region);
  const inRegion = (el: Element): boolean => {
    if (el.type === 'text' && el.kind === 'spine') return false;
    if (region === 'strip') return true;
    const cx = el.frame.xMm + el.frame.wMm / 2;
    return cx >= regionBox.xMm && cx < regionBox.xMm + regionBox.wMm;
  };
  const current = sortElements(doc.cover.elements);
  const kept = current.filter((el) => !inRegion(el));
  const replaced = current.filter(inRegion);

  const existingPhotos = opts.keepExisting ? replaced.filter((e): e is PhotoElement => e.type === 'photo' && e.photoId !== null) : [];
  const existingText = opts.keepExisting ? replaced.filter((e): e is TextElement => e.type === 'text') : [];
  const photoQueue = [...(opts.photoIds ?? [])];
  const textColor = coverTextColor(doc.cover);

  const elements: Element[] = [];
  for (const slot of layout.slots) {
    const frame = slotToFrame(slot, coverSlotBoxMm(doc, layout, slot, target));
    const slotId = `${layout.id}:${slot.id}`;
    if (slot.kind === 'photo') {
      const reuse = existingPhotos.shift();
      const photoId = reuse ? reuse.photoId : (photoQueue.shift() ?? null);
      elements.push(
        makePhotoElement({
          ...(reuse?.id ? { id: reuse.id } : {}),
          frame,
          photoId,
          slotId,
          z: elements.length,
          ...(reuse ? { fit: reuse.fit, adjustments: reuse.adjustments, style: reuse.style, caption: reuse.caption } : {}),
        }),
      );
    } else {
      const kind = ROLE_KIND[slot.role ?? 'body'];
      const carried = existingText.find((t) => t.kind === kind && (
        !slot.panel || (t.frame.xMm + t.frame.wMm / 2 >= coverPanelBoxMm(doc, slot.panel).xMm
          && t.frame.xMm + t.frame.wMm / 2 < coverPanelBoxMm(doc, slot.panel).xMm + coverPanelBoxMm(doc, slot.panel).wMm)
      ));
      if (carried) existingText.splice(existingText.indexOf(carried), 1);
      const base = slotTextStyle(doc.theme, slot.role);
      elements.push(
        makeTextElement({
          ...(carried?.id ? { id: carried.id } : {}),
          frame,
          kind,
          slotId,
          content: carried?.content ?? { paragraphs: [''] },
          style: carried?.style ?? { ...base, color: textColor, ...(slot.role === 'title' ? { fontSizePt: 34, fontWeight: 700 } : {}) },
          z: elements.length,
        }),
      );
    }
  }
  // Free text and decorations that have no matching template slot remain editable.
  if (opts.keepExisting) {
    kept.push(...existingText.filter((el) => el.slotId === null));
    kept.push(...replaced.filter((el) => el.type !== 'photo' && el.type !== 'text'));
  }
  return { elements, kept };
}

/**
 * The pages one application of `layout` writes to: the single page of a lone opening/closing
 * spread, the whole spread for a spread-scope template, or the chosen page (or both).
 */
export function layoutPages(spread: Pick<Spread, 'single'>, layout: Layout, page?: 'left' | 'right' | 'both'): ('left' | 'right' | undefined)[] {
  if (spread.single) return [spread.single];
  if (layout.scope === 'spread') return [undefined];
  const which = page ?? 'both';
  return which === 'both' ? ['left', 'right'] : [which];
}

/**
 * Which region an element belongs to. An element is assigned by its centre, so something straddling
 * the gutter counts as being on the page most of it sits on, and is never counted twice.
 */
export function frameBelongsTo(frame: Frame, box: BoxMm): boolean {
  const cx = frame.xMm + frame.wMm / 2;
  const cy = frame.yMm + frame.hMm / 2;
  return cx >= box.xMm && cx < box.xMm + box.wMm && cy >= box.yMm && cy < box.yMm + box.hMm;
}

export interface MaterializeOptions {
  photoIds?: string[];
  page?: 'left' | 'right' | 'both';
  /** reuse photos (and their fit/adjustments/style) and slot text of the spread's current elements */
  keepExisting?: boolean;
  /**
   * The tray, so a freshly placed picture can be cropped around its subject instead of its middle.
   * Without it every new photo is centre-cropped, exactly as before.
   */
  photos?: PhotoMap;
}

/**
 * The crop for a picture newly dropped into a slot: centred on its subject where that is possible.
 *
 * A cover crop can only slide as far as the picture overhangs the frame, so a subject near an edge
 * may not reach the middle. `focalImageFit` reports that rather than hiding it, and the caller can
 * ask again — `photoNeedsAttention` — to tell someone which pictures to look at.
 */
function slotFit(photos: PhotoMap | undefined, photoId: string | null, frame: Frame): ImageFit {
  const photo = photoId ? photos?.[photoId] : undefined;
  if (!photo?.focal) return neutralImageFit();
  return focalImageFit({
    boxWidth: frame.wMm,
    boxHeight: frame.hMm,
    naturalWidth: photo.width,
    naturalHeight: photo.height,
    fit: neutralImageFit(),
    focal: photo.focal,
  }).fit;
}

/**
 * Whether this picture's subject ended up away from the middle of its frame, and by how much.
 *
 * Returns null when there is nothing to say: no picture, no opinion about where its subject is, or
 * a subject that sits comfortably in the middle. A number means the crop did its best and fell
 * short, which is worth showing someone rather than silently printing a cropped-off head.
 */
export function photoOffCentre(photos: PhotoMap | undefined, el: PhotoElement): number | null {
  const photo = el.photoId ? photos?.[el.photoId] : undefined;
  if (!photo?.focal) return null;
  const r = focalImageFit({
    boxWidth: el.frame.wMm,
    boxHeight: el.frame.hMm,
    naturalWidth: photo.width,
    naturalHeight: photo.height,
    fit: el.fit,
    focal: photo.focal,
  });
  return r.centred ? null : r.missedBy;
}

/**
 * Convert a layout's fractional slots into concrete elements for a spread.
 * Photo slots are filled in order from `photoIds` (or from the spread's existing photo elements when
 * `keepExisting`); the remainder become empty slots. Text slots become TextElements by role.
 */
export function materializeLayout(doc: AlbumDocument, spreadIndex: number, layout: Layout, opts: MaterializeOptions = {}): Element[] {
  const spread = doc.spreads[spreadIndex];
  if (!spread) throw new Error(`spread ${spreadIndex} does not exist`);

  const pages = layoutPages(spread, layout, opts.page);
  const photoQueue = [...(opts.photoIds ?? [])];

  const out: Element[] = [];
  for (const page of pages) {
    const box = scopeBox(doc, spread, layout.scope, page);
    const suffix = pages.length > 1 ? `-${page}` : '';
    // Content is reused from the page being laid out, never from its neighbour: applying a
    // one-photo layout to the left page must not pull the right page's photo across, nor drop it
    // (docs/10-ux-audit.md · UX-04).
    const here = sortElements(spread.elements).filter((el) => frameBelongsTo(el.frame, box));
    const existingPhotos = opts.keepExisting ? here.filter((e): e is PhotoElement => e.type === 'photo' && e.photoId !== null) : [];
    const existingText = opts.keepExisting ? here.filter((e): e is TextElement => e.type === 'text' && e.slotId !== null) : [];
    for (const slot of layout.slots) {
      const frame = slotToFrame(slot, box);
      const slotId = `${layout.id}:${slot.id}${suffix}`;
      if (slot.kind === 'photo') {
        const reuse = existingPhotos.shift();
        const photoId = reuse ? reuse.photoId : (photoQueue.shift() ?? null);
        out.push(
          makePhotoElement({
            ...(reuse?.id ? { id: reuse.id } : {}),
            frame,
            photoId,
            slotId,
            ...(reuse
              ? { fit: reuse.fit, adjustments: reuse.adjustments, style: reuse.style, caption: reuse.caption }
              : { fit: slotFit(opts.photos, photoId, frame) }),
          }),
        );
      } else {
        const kind = ROLE_KIND[slot.role ?? 'body'];
        const carried = existingText.find((t) => t.kind === kind);
        if (carried) existingText.splice(existingText.indexOf(carried), 1);
        out.push(
          makeTextElement({
            ...(carried?.id ? { id: carried.id } : {}),
            frame,
            kind,
            slotId,
            content: carried?.content ?? { paragraphs: [''] },
            style: carried?.style ?? slotTextStyle(doc.theme, slot.role),
            z: 1,
          }),
        );
      }
    }
  }
  return out;
}

// ---------- rescale ----------

function scaleFrame(frame: Frame, sx: number, sy: number): Frame {
  return { ...frame, xMm: frame.xMm * sx, yMm: frame.yMm * sy, wMm: frame.wMm * sx, hMm: frame.hMm * sy };
}

/** Rescale element geometry only; typography, borders and radii are absolute and stay unchanged. */
function rescaleElements(elements: Element[], sx: number, sy: number): Element[] {
  if (sx === 1 && sy === 1) return elements;
  return elements.map((el) => ({ ...el, frame: scaleFrame(el.frame, sx, sy) }));
}

// ---------- theme ----------

/**
 * Switching the album style (UX-34).
 *
 * Elements carry explicit styles — there is no "inherit from the theme" flag to flip — so a theme
 * change could only ever update the DEFAULTS for new content, which is not what the settings copy
 * promised. `restyle` closes that gap by translating the content that still speaks the OUTGOING
 * theme: a colour that is exactly a palette entry moves to the same entry of the new palette, and
 * text set in the outgoing heading/body font moves to the new one. Anything the user picked
 * themselves — a hand-chosen colour, an uploaded font — matches nothing and is left alone, which is
 * how deliberate overrides survive. One command, so undo restores the previous appearance whole.
 */
interface ThemeTranslation {
  color: (c: string) => string;
  font: (f: string) => string;
  empty: boolean;
}

function themeTranslation(from: AlbumTheme, to: AlbumTheme): ThemeTranslation {
  const key = (c: string) => c.trim().toLowerCase();
  const colors = new Map<string, string>();
  from.palette.forEach((c, i) => {
    const next = to.palette[i];
    if (next && key(next) !== key(c) && !colors.has(key(c))) colors.set(key(c), next);
  });
  const fonts = new Map<string, string>();
  if (from.headingFont !== to.headingFont) fonts.set(from.headingFont, to.headingFont);
  if (from.bodyFont !== to.bodyFont && !fonts.has(from.bodyFont)) fonts.set(from.bodyFont, to.bodyFont);
  return {
    color: (c) => colors.get(key(c)) ?? c,
    font: (f) => fonts.get(f) ?? f,
    empty: colors.size === 0 && fonts.size === 0,
  };
}

function restyleTextStyle(style: TextStyle, tr: ThemeTranslation): TextStyle {
  const fontFamily = tr.font(style.fontFamily);
  const color = tr.color(style.color);
  const backgroundColor = style.backgroundColor === null ? null : tr.color(style.backgroundColor);
  if (fontFamily === style.fontFamily && color === style.color && backgroundColor === style.backgroundColor) return style;
  return { ...style, fontFamily, color, backgroundColor };
}

function restyleBackground(bg: Background, tr: ThemeTranslation): Background {
  switch (bg.type) {
    case 'color': {
      const color = tr.color(bg.color);
      return color === bg.color ? bg : { ...bg, color };
    }
    case 'gradient': {
      const from = tr.color(bg.from);
      const to = tr.color(bg.to);
      return from === bg.from && to === bg.to ? bg : { ...bg, from, to };
    }
    case 'asset': {
      if (!bg.tint) return bg;
      const tint = tr.color(bg.tint);
      return tint === bg.tint ? bg : { ...bg, tint };
    }
    // A photo background is the user's own image; the theme has nothing to say about it.
    default:
      return bg;
  }
}

function restyleElement(el: Element, tr: ThemeTranslation): Element {
  switch (el.type) {
    case 'text': {
      const style = restyleTextStyle(el.style, tr);
      return style === el.style ? el : { ...el, style };
    }
    case 'photo': {
      const borderColor = tr.color(el.style.borderColor);
      const style = borderColor === el.style.borderColor ? el.style : { ...el.style, borderColor };
      const captionStyle = el.caption ? restyleTextStyle(el.caption.style, tr) : null;
      const caption = el.caption && captionStyle !== el.caption.style ? { ...el.caption, style: captionStyle! } : el.caption;
      return style === el.style && caption === el.caption ? el : { ...el, style, caption };
    }
    case 'sticker': {
      if (!el.tint) return el;
      const tint = tr.color(el.tint);
      return tint === el.tint ? el : { ...el, tint };
    }
    default: {
      const fill = el.fill === null ? null : tr.color(el.fill);
      const stroke = el.stroke === null ? null : tr.color(el.stroke);
      return fill === el.fill && stroke === el.stroke ? el : { ...el, fill, stroke };
    }
  }
}

function restyleElements(elements: Element[], tr: ThemeTranslation): Element[] {
  let changed = false;
  const next = elements.map((el) => {
    const mapped = restyleElement(el, tr);
    if (mapped !== el) changed = true;
    return mapped;
  });
  return changed ? next : elements;
}

/** Retarget everything that still follows `from` onto `to`. Untouched subtrees stay by reference. */
/** The schema's own defaults: what a page or cover falls back to when the new style paints nothing. */
const WHITE_PAGE: Background = { type: 'color', color: '#ffffff' };
const DEFAULT_COVER: Background = { type: 'color', color: '#1b1b1f' };

/** Whether two backgrounds are the same paint. A photo is the user's own and never a style's. */
function sameBackground(a: Background, b: Background): boolean {
  const key = (c: string | null) => (c ?? '').trim().toLowerCase();
  switch (a.type) {
    case 'color':
      return b.type === 'color' && key(a.color) === key(b.color);
    case 'gradient':
      return b.type === 'gradient' && key(a.from) === key(b.from) && key(a.to) === key(b.to) && a.angleDeg === b.angleDeg;
    case 'asset':
      return b.type === 'asset' && a.assetId === b.assetId && key(a.tint) === key(b.tint) && a.opacity === b.opacity && a.mode === b.mode;
    default:
      return false;
  }
}

function restyleToTheme(doc: AlbumDocument, from: AlbumTheme, to: AlbumTheme): AlbumDocument {
  const tr = themeTranslation(from, to);
  if (tr.empty && !from.pageBackground && !from.coverBackground) return doc;

  // A page or cover still wearing the outgoing style's own background takes the new style's; one
  // the user painted goes through the palette mapping like everything else.
  const swap = (bg: Background, own: Background | null, next: Background | null, fallback: Background): Background => {
    if (!own || !sameBackground(bg, own)) return restyleBackground(bg, tr);
    const replacement = next ?? fallback;
    return sameBackground(bg, replacement) ? bg : replacement;
  };
  const page = (bg: Background) => swap(bg, from.pageBackground, to.pageBackground, WHITE_PAGE);

  let changed = false;
  const spreads = doc.spreads.map((spread) => {
    const elements = restyleElements(spread.elements, tr);
    const background = page(spread.background);
    const backgroundRight = spread.backgroundRight ? page(spread.backgroundRight) : spread.backgroundRight;
    if (elements === spread.elements && background === spread.background && backgroundRight === spread.backgroundRight) {
      return spread;
    }
    changed = true;
    return { ...spread, elements, background, backgroundRight };
  });

  const coverElements = restyleElements(doc.cover.elements, tr);
  const coverBackground = swap(doc.cover.background, from.coverBackground, to.coverBackground, DEFAULT_COVER);
  const spineBackground = doc.cover.spineBackground ? restyleBackground(doc.cover.spineBackground, tr) : doc.cover.spineBackground;
  const coverChanged =
    coverElements !== doc.cover.elements ||
    coverBackground !== doc.cover.background ||
    spineBackground !== doc.cover.spineBackground;

  if (!changed && !coverChanged) return doc;
  return {
    ...doc,
    spreads: changed ? spreads : doc.spreads,
    cover: coverChanged
      ? { ...doc.cover, elements: coverElements, background: coverBackground, spineBackground }
      : doc.cover,
  };
}

// ---------- page count ----------

export function spreadCountForPages(innerPages: number, firstPageSingle: boolean): number {
  return firstPageSingle ? innerPages / 2 + 1 : innerPages / 2;
}

/**
 * Page counts are even. This is the SLIDER rule — snap to the NEAREST even number, so nudging the
 * control down actually makes the album shorter. `createAlbumDocument` uses `evenInnerPages`
 * instead, which always rounds UP, because a caller asking for 21 pages must not get 20.
 */
export function clampInnerPages(n: number): number {
  const even = Math.round(n / 2) * 2;
  return Math.min(PAGE_COUNT_LIMITS.max, Math.max(PAGE_COUNT_LIMITS.min, even));
}

// ---------- reorder ----------

function reorder(elements: Element[], id: string, to: 'front' | 'back' | 'forward' | 'backward'): Element[] {
  const sorted = sortElements(elements);
  const from = sorted.findIndex((e) => e.id === id);
  if (from < 0) return elements;
  const target = to === 'front' ? sorted.length - 1 : to === 'back' ? 0 : to === 'forward' ? Math.min(sorted.length - 1, from + 1) : Math.max(0, from - 1);
  if (target === from) return elements;
  const [el] = sorted.splice(from, 1);
  sorted.splice(target, 0, el!);
  // Re-number z in visual order; array order follows visual order too.
  return sorted.map((e, i) => (e.z === i ? e : { ...e, z: i }));
}

// ---------- apply ----------

export function applyCommand(doc: AlbumDocument, cmd: Command): AlbumDocument {
  switch (cmd.type) {
    case 'batch':
      return cmd.commands.reduce(applyCommand, doc);

    case 'spread.add': {
      const index = Math.max(0, Math.min(doc.spreads.length, cmd.index));
      const spread = cmd.spread ? SpreadSchema.parse(cmd.spread) : emptySpread(doc.theme.pageBackground);
      const spreads = doc.spreads.slice();
      spreads.splice(index, 0, spread);
      return normalizeSingles({ ...doc, spreads });
    }

    case 'spread.remove': {
      requireSpread(doc, cmd.index, cmd);
      if (doc.spreads.length <= 1) throw new CommandError('cannot remove the last spread', cmd);
      const spreads = doc.spreads.filter((_, i) => i !== cmd.index);
      return normalizeSingles({ ...doc, spreads });
    }

    case 'spread.move': {
      const spread = requireSpread(doc, cmd.from, cmd);
      const to = Math.max(0, Math.min(doc.spreads.length - 1, cmd.to));
      if (to === cmd.from) return doc;
      const spreads = doc.spreads.filter((_, i) => i !== cmd.from);
      spreads.splice(to, 0, spread);
      return normalizeSingles({ ...doc, spreads });
    }

    case 'spread.setBackground': {
      if (cmd.index === 'cover') return { ...doc, cover: { ...doc.cover, background: cmd.background } };
      const spread = requireSpread(doc, cmd.index, cmd);
      const next: Spread =
        cmd.side === 'right'
          ? { ...spread, backgroundRight: cmd.background }
          : { ...spread, background: cmd.background, backgroundRight: null };
      return withSpread(doc, cmd.index, next);
    }

    case 'album.setPrint': {
      const patch = stripUndefined(cmd.patch);
      const next = { ...doc.print, ...patch };
      if (next.service === doc.print.service && next.binding === doc.print.binding) return doc;
      return { ...doc, print: next };
    }

    case 'spread.setTags': {
      const spread = requireSpread(doc, cmd.index, cmd);
      const tags = normalizeTags(cmd.tags, TAG_LIMITS.maxPerSpread);
      if (sameTags(tags, spread.tags)) return doc;
      return withSpread(doc, cmd.index, { ...spread, tags });
    }

    case 'spread.applyLayout': {
      const spread = requireSpread(doc, cmd.index, cmd);
      const materialized = materializeLayout(doc, cmd.index, cmd.layout, cmd);
      // Only the page(s) the template is applied to are rewritten. Whatever sits on the other page
      // of the spread keeps its id, content, crop, style and position (audit UX-04).
      const boxes = layoutPages(spread, cmd.layout, cmd.page).map((page) => scopeBox(doc, spread, cmd.layout.scope, page));
      const kept = spread.elements.filter((el) => {
        if (!boxes.some((box) => frameBelongsTo(el.frame, box))) return true;
        if (!cmd.keepExisting) return false;
        // Inside the region, slots are replaced by the new ones; free text and decoration stay.
        return el.type !== 'photo' && !(el.type === 'text' && el.slotId !== null);
      });
      return withSpread(doc, cmd.index, { ...spread, layoutId: cmd.layout.id, elements: [...materialized, ...kept] });
    }

    case 'cover.applyLayout': {
      const { elements, kept } = materializeCoverLayout(doc, cmd.layout, cmd.target, { photoIds: cmd.photoIds, keepExisting: cmd.keepExisting });
      return { ...doc, cover: { ...doc.cover, layoutId: cmd.layout.id, elements: [...kept, ...elements] } };
    }

    case 'element.add': {
      const elements = getElements(doc, cmd.target, cmd);
      if (elements.some((e) => e.id === cmd.element.id)) throw new CommandError(`duplicate element id ${cmd.element.id}`, cmd);
      return withElements(doc, cmd.target, [...elements, cmd.element], cmd);
    }

    case 'element.update': {
      const elements = getElements(doc, cmd.target, cmd);
      const i = elements.findIndex((e) => e.id === cmd.id);
      if (i < 0) throw new CommandError(`element ${cmd.id} not found`, cmd);
      const { type: _t, id: _i, ...patch } = cmd.patch as Record<string, unknown>;
      const merged = deepMerge(elements[i]!, patch as DeepPartial<Element>);
      return withElements(doc, cmd.target, replaceAt(elements, i, merged), cmd);
    }

    case 'element.remove': {
      const elements = getElements(doc, cmd.target, cmd);
      const ids = new Set(cmd.ids);
      const next = elements.filter((e) => !ids.has(e.id));
      return next.length === elements.length ? doc : withElements(doc, cmd.target, next, cmd);
    }

    case 'element.reorder': {
      const elements = getElements(doc, cmd.target, cmd);
      return withElements(doc, cmd.target, reorder(elements, cmd.id, cmd.to), cmd);
    }

    case 'element.moveToSpread': {
      const from = requireSpread(doc, cmd.from, cmd);
      requireSpread(doc, cmd.to, cmd);
      const el = from.elements.find((e) => e.id === cmd.id);
      if (!el) throw new CommandError(`element ${cmd.id} not found`, cmd);
      if (cmd.from === cmd.to) return doc;
      const without = withSpread(doc, cmd.from, { ...from, elements: from.elements.filter((e) => e.id !== cmd.id) });
      const to = without.spreads[cmd.to]!;
      return withSpread(without, cmd.to, { ...to, elements: [...to.elements, el] });
    }

    case 'album.setFormat': {
      const sx = cmd.page.widthMm / doc.page.widthMm;
      const sy = cmd.page.heightMm / doc.page.heightMm;
      const coverOld = doc.page.widthMm * 2 + doc.cover.spineWidthMm;
      const coverNew = cmd.page.widthMm * 2 + doc.cover.spineWidthMm;
      const cx = coverNew / coverOld;
      return {
        ...doc,
        format: cmd.format,
        page: { widthMm: cmd.page.widthMm, heightMm: cmd.page.heightMm },
        cover: { ...doc.cover, elements: rescaleElements(doc.cover.elements, cx, sy) },
        spreads: doc.spreads.map((s) => (sx === 1 && sy === 1 ? s : { ...s, elements: rescaleElements(s.elements, sx, sy) })),
      };
    }

    case 'album.setPageCount': {
      const innerPages = clampInnerPages(cmd.innerPages);
      const want = spreadCountForPages(innerPages, doc.firstPageSingle);
      if (want === doc.spreads.length) return doc;
      const spreads = doc.spreads.slice(0, want);
      while (spreads.length < want) spreads.push(emptySpread(doc.theme.pageBackground));
      const next = normalizeSingles({ ...doc, spreads });
      // A thicker book has a wider spine. Only when the user has not pinned the width themselves.
      return doc.cover.spineWidthMode === 'auto' ? withSpineWidth(next, defaultSpineWidthMm(innerPageCount(next))) : next;
    }

    case 'album.setMeta':
      return { ...doc, meta: { ...doc.meta, ...stripUndefined(cmd.patch) } };

    case 'album.setTheme': {
      const theme = { ...doc.theme, ...stripUndefined(cmd.patch) };
      const next = { ...doc, theme };
      // Defaults-only by default: existing content keeps every style it was given.
      return cmd.restyle ? restyleToTheme(next, doc.theme, theme) : next;
    }

    case 'cover.set': {
      const patch = stripUndefined(cmd.patch);
      let next: AlbumDocument = { ...doc, cover: { ...doc.cover, ...patch } };
      // Switching back to 'auto' immediately re-derives the width; a pinned width stays as set.
      if (patch.spineWidthMode === 'auto' && patch.spineWidthMm === undefined) {
        next = withSpineWidth(next, defaultSpineWidthMm(innerPageCount(next)));
      } else if (patch.spineWidthMm !== undefined && patch.spineWidthMm !== doc.cover.spineWidthMm) {
        next = withSpineWidth({ ...next, cover: { ...next.cover, spineWidthMm: doc.cover.spineWidthMm } }, patch.spineWidthMm);
      }
      // Text on the spine follows the reading direction, whether set directly or through the binding.
      if (spineTextDirection(next) !== spineTextDirection(doc)) {
        const rotationDeg = spineRotationDeg(spineTextDirection(next));
        next = {
          ...next,
          cover: {
            ...next.cover,
            elements: next.cover.elements.map((el) =>
              el.type === 'text' && el.kind === 'spine' ? { ...el, frame: { ...el.frame, rotationDeg } } : el,
            ),
          },
        };
      }
      return next;
    }
  }
}

/**
 * Change the spine width and keep the artwork where the eye expects it: elements on the back panel
 * slide with the panel, elements centred on the spine stay centred (spine text also takes the new
 * width), and the front panel does not move.
 */
function withSpineWidth(doc: AlbumDocument, spineWidthMm: number): AlbumDocument {
  const old = doc.cover.spineWidthMm;
  if (spineWidthMm === old) return doc;
  const delta = spineWidthMm - old;
  const W = doc.page.widthMm;
  const elements = doc.cover.elements.map((el) => {
    const cx = el.frame.xMm + el.frame.wMm / 2;
    const isSpineText = el.type === 'text' && el.kind === 'spine';
    if (isSpineText) {
      // Horizontal box rotated about its centre: `hMm` is the across-spine dimension.
      const hMm = el.frame.hMm === old ? spineWidthMm : el.frame.hMm;
      const cy = el.frame.yMm + el.frame.hMm / 2;
      return { ...el, frame: { ...el.frame, hMm, xMm: el.frame.xMm + delta / 2, yMm: cy - hMm / 2 } };
    }
    if (cx > W + old) return { ...el, frame: { ...el.frame, xMm: el.frame.xMm + delta } };
    if (cx >= W) return { ...el, frame: { ...el.frame, xMm: el.frame.xMm + delta / 2 } };
    return el;
  });
  return { ...doc, cover: { ...doc.cover, spineWidthMm, elements } };
}

function stripUndefined<T extends object>(patch: T): T {
  return Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined)) as T;
}
